import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams, useLocation } from 'react-router-dom';
import PortfolioSiteLayout from '@/components/portfolio/PortfolioSiteLayout';
import { portfolioService, type PublicPortfolioResponse } from '@/services/portfolio';
import { resolveIntroVideoEmbed } from '@/utils/videoEmbed';
import { getMarketingSiteOrigin } from '@/utils/hostOrigins';

const PortfolioPage: React.FC = () => {
  const { portfolioSlug } = useParams<{ portfolioSlug: string }>();
  const location = useLocation();
  const [portfolio, setPortfolio] = useState<PublicPortfolioResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
  });

  useEffect(() => {
    const loadPortfolio = async () => {
      if (!portfolioSlug) {
        setError('Portfolio not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await portfolioService.getPublicPortfolio(portfolioSlug);
        setPortfolio(response);
        setError(null);
      } catch (err: unknown) {
        setPortfolio(null);
        const msg =
          err &&
          typeof err === 'object' &&
          'response' in err &&
          err.response &&
          typeof err.response === 'object' &&
          'data' in err.response &&
          err.response.data &&
          typeof err.response.data === 'object' &&
          'error' in err.response.data
            ? String((err.response.data as { error?: string }).error)
            : 'Portfolio not found';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    void loadPortfolio();
  }, [portfolioSlug]);

  useEffect(() => {
    if (loading || !portfolio) {
      return;
    }
    const id = location.hash.replace(/^#/, '').trim();
    if (!id) {
      return;
    }
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(t);
  }, [loading, portfolio, location.hash]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!portfolioSlug) {
      return;
    }

    try {
      setSubmitting(true);
      await portfolioService.requestAccess(portfolioSlug, {
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        email: formData.email.trim(),
        message: formData.message || undefined,
      });
      setSuccess('Your message has been sent. The teacher will review your request.');
      setFormData({ firstName: '', lastName: '', email: '', message: '' });
    } catch (err: unknown) {
      setSuccess(null);
      const msg =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'error' in err.response.data
          ? String((err.response.data as { error?: string }).error)
          : 'Failed to submit access request';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="public-site-page">
        <div className="public-site">
          <div className="pub-empty">Loading…</div>
        </div>
      </div>
    );
  }

  if (error || !portfolio) {
    return (
      <div className="public-site-page">
        <div className="public-site">
          <div className="pub-state-card">
            <h1>Site not available</h1>
            <p>{error || 'This page is not published yet.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const slug = portfolio.slug;
  const base = `/${encodeURIComponent(slug)}`;
  const teacherName =
    [portfolio.teacherFirstName, portfolio.teacherLastName].filter(Boolean).join(' ') ||
    portfolio.tenantName;

  const introEmbed = portfolio.introVideoUrl ? resolveIntroVideoEmbed(portfolio.introVideoUrl) : null;
  const hasBlogs = Boolean(portfolio.blogs && portfolio.blogs.length > 0);
  const marketingOrigin = getMarketingSiteOrigin().replace(/^https?:\/\//, '');
  const footerDomain = `${marketingOrigin}/${slug}`;

  const heroTitle = portfolio.headline || `Lessons with ${teacherName}`;
  const heroAbout =
    portfolio.aboutText?.trim() ||
    'Your public site — lessons, posts, and booking in one branded place.';

  return (
    <>
      <Helmet>
        <title>
          {portfolio.headline || teacherName} | {portfolio.tenantName}
        </title>
      </Helmet>

      <PortfolioSiteLayout
        portfolioSlug={slug}
        siteTitle={portfolio.tenantName}
        tagline={teacherName}
        avatarUrl={portfolio.teacherAvatarUrl}
        avatarAlt={teacherName}
        footerDomain={footerDomain}
        showBlogLink={hasBlogs}
        showVideoLink={Boolean(portfolio.introVideoUrl)}
        showPricingLink={Boolean(portfolio.pricingText)}
      >
        <div id="about" className="pub-hero scroll-mt-0">
          <h1>{heroTitle}</h1>
          <p>{heroAbout}</p>
          <div className="pub-hero-actions">
            <a href="#contact" className="pub-cta">
              {portfolio.ctaText?.trim() || 'Book your first lesson'}
            </a>
            {(hasBlogs || portfolio.introVideoUrl) && (
              <a
                href={hasBlogs ? '#blog' : '#video'}
                className="pub-cta pub-cta--secondary"
              >
                Learn more
              </a>
            )}
          </div>
        </div>

        <div className={`pub-body${!portfolio.introVideoUrl ? ' pub-body--single' : ''}`}>
          {portfolio.introVideoUrl && (
            <div id="video" className="pub-vid scroll-mt-0">
              {introEmbed ? (
                <iframe
                  title={`Intro — ${teacherName}`}
                  src={introEmbed.embedUrl}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : (
                <div className="pub-vid-fallback">
                  <p>
                    Open video:{' '}
                    <a href={portfolio.introVideoUrl} target="_blank" rel="noreferrer">
                      {portfolio.introVideoUrl}
                    </a>
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="pub-cards">
            {hasBlogs &&
              portfolio.blogs!.slice(0, 4).map((post) => (
                <Link
                  key={post.slug}
                  to={`${base}/blogs/${encodeURIComponent(post.slug)}`}
                  className="pub-card"
                >
                  <div className="pt">From the blog</div>
                  <div className="pd">{post.title}</div>
                  {post.publishedAt && (
                    <div className="pm">
                      {new Date(post.publishedAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </div>
                  )}
                </Link>
              ))}

            {portfolio.pricingText && (
              <a href="#pricing" className="pub-card">
                <div className="pt">Lesson plans</div>
                <div className="pd">Rates and packages — see pricing details</div>
              </a>
            )}

            {!hasBlogs && !portfolio.pricingText && (
              <div className="pub-card">
                <div className="pt">Get in touch</div>
                <div className="pd">Send a message to book your first lesson.</div>
              </div>
            )}
          </div>
        </div>

        {portfolio.pricingText && (
          <section id="pricing" className="pub-section scroll-mt-0">
            <p className="pub-section-label">Plans</p>
            <h2>Pricing</h2>
            <div className="pub-pricing-box">{portfolio.pricingText}</div>
          </section>
        )}

        {hasBlogs && portfolio.blogs!.length > 4 && (
          <section id="blog" className="pub-section scroll-mt-0">
            <p className="pub-section-label">Writing</p>
            <h2>Blog</h2>
            <p className="pub-section-desc">Updates from {teacherName}.</p>
            <div className="pub-cards">
              {portfolio.blogs!.map((post) => (
                <Link
                  key={post.slug}
                  to={`${base}/blogs/${encodeURIComponent(post.slug)}`}
                  className="pub-card"
                >
                  <div className="pt">{post.title}</div>
                  {post.excerpt && (
                    <div
                      className="pd line-clamp-2 [&_img]:hidden"
                      dangerouslySetInnerHTML={{ __html: post.excerpt }}
                    />
                  )}
                </Link>
              ))}
            </div>
          </section>
        )}

        {hasBlogs && portfolio.blogs!.length <= 4 && (
          <section id="blog" className="sr-only" aria-hidden>
            Blog
          </section>
        )}

        <section id="contact" className="pub-section scroll-mt-0">
          <p className="pub-section-label">Contact</p>
          <h2>{portfolio.ctaText?.trim() || 'Request access'}</h2>
          <p className="pub-section-desc">Send a message — {teacherName} will get back to you.</p>
          <form className="pub-form" onSubmit={onSubmit}>
            <div className="pub-form-row">
              <input
                className="pub-input"
                placeholder="First name"
                value={formData.firstName}
                onChange={(event) => setFormData((prev) => ({ ...prev, firstName: event.target.value }))}
              />
              <input
                className="pub-input"
                placeholder="Last name"
                value={formData.lastName}
                onChange={(event) => setFormData((prev) => ({ ...prev, lastName: event.target.value }))}
              />
            </div>
            <input
              required
              type="email"
              className="pub-input"
              placeholder="Email"
              value={formData.email}
              onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))}
            />
            <textarea
              className="pub-textarea"
              placeholder="What are you looking for?"
              value={formData.message}
              onChange={(event) => setFormData((prev) => ({ ...prev, message: event.target.value }))}
            />
            <button type="submit" className="pub-cta" disabled={submitting}>
              {submitting ? 'Sending…' : portfolio.ctaText?.trim() || 'Send message'}
            </button>
            {success && <p className="pub-success">{success}</p>}
          </form>
        </section>
      </PortfolioSiteLayout>
    </>
  );
};

export default PortfolioPage;
